import { createContext, useContext, useState, useCallback, useLayoutEffect } from "react";

/**
 * Broadcasts whether the current page is currently showing the "Research 1617"
 * sub-brand skin (black + brass gold). Turning it on swaps the Nav wordmark
 * and lets pages render a dark palette.
 *
 * Pages that opt in when tier === select:
 *   • Catalog (Select tab) and Select product-detail pages
 *   • Cart page — when `isSelectCart` (iter156.5.3)
 *   • Checkout page — when `isSelectCart` (iter156.5)
 * Admin, Landing, and the Chooser page are BBK-branded and MUST NOT set this.
 */
const SelectThemeContext = createContext({
  isSelect: false,
  setSelect: () => {},
});

export function SelectThemeProvider({ children }) {
  const [isSelect, setIsSelect] = useState(false);

  const setSelect = useCallback((next) => {
    setIsSelect(Boolean(next));
  }, []);

  return (
    <SelectThemeContext.Provider value={{ isSelect, setSelect }}>
      {children}
    </SelectThemeContext.Provider>
  );
}

export function useSelectTheme() {
  return useContext(SelectThemeContext);
}

/**
 * Convenience hook — call from a page's top-level component with the boolean
 * that describes whether the page is currently in Select mode. Resets to false
 * on unmount so navigating away always leaves the app un-themed.
 *
 * iter_142 — uses `useLayoutEffect` (not `useEffect`) so the theme switch is
 * committed BEFORE the next browser paint. This prevents the bright-white
 * "bone" flash you otherwise see when navigating between two Select-themed
 * routes (catalog → product detail): with the effect version, React commits
 * the DOM in a light state, paints once, then re-commits in dark. With the
 * layout version, both the outgoing cleanup and the incoming setup run
 * inside the same synchronous phase, so only the final DARK state is ever
 * painted.
 */
export function useSetSelectTheme(active) {
  const { setSelect } = useSelectTheme();
  useLayoutEffect(() => {
    setSelect(!!active);
    return () => setSelect(false);
  }, [active, setSelect]);
}
